// src/platform/storage/indexedDb.js

import {
  configuration,
  eventBus,
  logger
} from '@/platform/core'


class IndexedDbManager {
  constructor() {
    this.dbName = null
    this.version = null

    this.db = null
    this.openPromise = null
  }


  // ==========================================================
  // CONFIGURATION
  // ==========================================================

  configure() {
    if (this.db) {
      throw new Error(
        '[IndexedDbManager] Database is already open.'
      )
    }

    this.dbName =
      configuration.get(
        'storage.dbName',
        'NexgbitsIS'
      )

    const configuredVersion =
      Number(
        configuration.get(
          'storage.dbVersion',
          3
        )
      )

    /*
     * POS offline sessions require schema version 3.
     *
     * Math.max protects existing deployments whose platform
     * configuration may still specify dbVersion 1 or 2.
     */
    this.version =
      Math.max(
        Number.isFinite(
          configuredVersion
        )
          ? configuredVersion
          : 3,
        3
      )
  }


  // ==========================================================
  // OPEN DATABASE
  // ==========================================================

  async open() {
    if (!this.isSupported()) {
      const error =
        new Error(
          'IndexedDB is not supported by this browser.'
        )

      logger.error(
        'IndexedDB is unavailable.',
        {
          error
        }
      )

      eventBus.emit(
        'storage.indexeddb.unsupported',
        {
          error,

          timestamp:
            new Date()
              .toISOString()
        }
      )

      throw error
    }

    if (this.db) {
      return this.db
    }

    if (this.openPromise) {
      return this.openPromise
    }

    if (
      !this.dbName ||
      !this.version
    ) {
      this.configure()
    }

    logger.info(
      'Opening IndexedDB database.',
      {
        dbName:
          this.dbName,

        version:
          this.version
      }
    )

    this.openPromise =
      new Promise(
        (
          resolve,
          reject
        ) => {
          const request =
            indexedDB.open(
              this.dbName,
              this.version
            )

          // ================================================
          // UPGRADE
          // ================================================

          request.onupgradeneeded =
            event => {
              const db =
                event.target.result

              const oldVersion =
                event.oldVersion

              const newVersion =
                event.newVersion

              logger.info(
                'IndexedDB upgrade started.',
                {
                  dbName:
                    this.dbName,

                  oldVersion,

                  newVersion
                }
              )

              this.applyMigrations(
                db,
                oldVersion,
                newVersion
              )

              eventBus.emit(
                'storage.indexeddb.upgrading',
                {
                  dbName:
                    this.dbName,

                  oldVersion,

                  newVersion
                }
              )
            }

          // ================================================
          // SUCCESS
          // ================================================

          request.onsuccess =
            event => {
              this.db =
                event.target.result

              this.db.onversionchange =
                () => {
                  logger.warn(
                    'IndexedDB version changed in another context. Closing current connection.'
                  )

                  this.close()

                  eventBus.emit(
                    'storage.indexeddb.versionchanged',
                    {
                      dbName:
                        this.dbName
                    }
                  )
                }

              logger.info(
                'IndexedDB opened successfully.',
                {
                  dbName:
                    this.dbName,

                  version:
                    this.db.version
                }
              )

              eventBus.emit(
                'storage.indexeddb.ready',
                {
                  dbName:
                    this.dbName,

                  version:
                    this.db.version
                }
              )

              resolve(
                this.db
              )
            }

          // ================================================
          // ERROR
          // ================================================

          request.onerror =
            () => {
              const error =
                request.error ||
                new Error(
                  'Failed to open IndexedDB database.'
                )

              logger.error(
                'IndexedDB open failed.',
                {
                  dbName:
                    this.dbName,

                  error
                }
              )

              eventBus.emit(
                'storage.indexeddb.error',
                {
                  operation:
                    'open',

                  error
                }
              )

              this.openPromise =
                null

              reject(
                error
              )
            }

          // ================================================
          // BLOCKED
          // ================================================

          request.onblocked =
            () => {
              logger.warn(
                'IndexedDB upgrade is blocked by another open browser context.',
                {
                  dbName:
                    this.dbName,

                  version:
                    this.version
                }
              )

              eventBus.emit(
                'storage.indexeddb.blocked',
                {
                  dbName:
                    this.dbName,

                  version:
                    this.version
                }
              )
            }
        }
      )

    return this.openPromise
  }


  // ==========================================================
  // MIGRATIONS
  // ==========================================================

  applyMigrations(
    db,
    oldVersion,
    newVersion
  ) {
    if (
      oldVersion < 1
    ) {
      this.createVersion1Schema(
        db
      )
    }

    if (
      oldVersion < 2
    ) {
      this.createVersion2Schema(
        db
      )
    }

    if (
      oldVersion < 3
    ) {
      this.createVersion3Schema(
        db
      )
    }

    logger.info(
      'IndexedDB migrations applied.',
      {
        oldVersion,
        newVersion
      }
    )
  }


  // ==========================================================
  // VERSION 1 SCHEMA
  //
  // Original platform storage:
  //
  // - generic cache
  // - synchronization queue
  // ==========================================================

  createVersion1Schema(
    db
  ) {
    // ========================================================
    // CACHE
    // ========================================================

    if (
      !db.objectStoreNames.contains(
        'cache'
      )
    ) {
      db.createObjectStore(
        'cache',
        {
          keyPath:
            'key'
        }
      )
    }

    // ========================================================
    // SYNC QUEUE
    // ========================================================

    if (
      !db.objectStoreNames.contains(
        'syncQueue'
      )
    ) {
      const syncQueueStore =
        db.createObjectStore(
          'syncQueue',
          {
            keyPath:
              'id',

            autoIncrement:
              true
          }
        )

      syncQueueStore.createIndex(
        'status',
        'status',
        {
          unique:
            false
        }
      )

      syncQueueStore.createIndex(
        'createdAt',
        'createdAt',
        {
          unique:
            false
        }
      )
    }
  }


  // ==========================================================
  // VERSION 2 SCHEMA
  //
  // Durable POS transaction storage.
  //
  // This store holds unfinished/local POS transactions so they
  // survive:
  //
  // - browser refresh
  // - PWA restart
  // - connectivity loss
  //
  // syncQueue remains responsible for work waiting to be sent
  // to the ASP.NET backend.
  // ==========================================================

  createVersion2Schema(
    db
  ) {
    if (
      db.objectStoreNames.contains(
        'posTransactions'
      )
    ) {
      return
    }

    const store =
      db.createObjectStore(
        'posTransactions',
        {
          keyPath:
            'key'
        }
      )

    // ========================================================
    // TENANT
    // ========================================================

    store.createIndex(
      'tenantId',
      'tenantId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // TERMINAL
    // ========================================================

    store.createIndex(
      'terminalId',
      'terminalId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // SESSION
    // ========================================================

    store.createIndex(
      'sessionId',
      'sessionId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // IDEMPOTENT TRANSACTION ID
    // ========================================================

    store.createIndex(
      'clientTransactionId',
      'clientTransactionId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // LOCAL STATUS
    // ========================================================

    store.createIndex(
      'status',
      'status',
      {
        unique:
          false
      }
    )

    // ========================================================
    // UPDATED
    // ========================================================

    store.createIndex(
      'updatedAtUtc',
      'updatedAtUtc',
      {
        unique:
          false
      }
    )
  }


  // ==========================================================
  // VERSION 3 SCHEMA
  //
  // Durable local POS cashier sessions.
  //
  // Offline POS operation needs to support:
  //
  // 1. selecting a cached terminal
  // 2. opening a cashier session without network connectivity
  // 3. creating transactions against that local session
  // 4. synchronizing the session when connectivity returns
  // 5. replacing/mapping the local clientSessionId to the
  //    authoritative backend serverSessionId
  //
  // IMPORTANT:
  //
  // clientSessionId is the durable local identity.
  //
  // serverSessionId is null until the offline session has been
  // successfully synchronized with the ASP.NET backend.
  // ==========================================================

  createVersion3Schema(
    db
  ) {
    if (
      db.objectStoreNames.contains(
        'posSessions'
      )
    ) {
      return
    }

    const store =
      db.createObjectStore(
        'posSessions',
        {
          keyPath:
            'clientSessionId'
        }
      )

    // ========================================================
    // TERMINAL
    // ========================================================

    store.createIndex(
      'terminalId',
      'terminalId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // LOCATION
    // ========================================================

    store.createIndex(
      'locationId',
      'locationId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // SERVER SESSION
    //
    // Null until successfully synchronized.
    // ========================================================

    store.createIndex(
      'serverSessionId',
      'serverSessionId',
      {
        unique:
          false
      }
    )

    // ========================================================
    // SESSION STATUS
    //
    // Examples:
    //
    // OPEN
    // CLOSED
    // ========================================================

    store.createIndex(
      'status',
      'status',
      {
        unique:
          false
      }
    )

    // ========================================================
    // SYNCHRONIZATION STATUS
    //
    // Examples:
    //
    // PENDING
    // QUEUED
    // SYNCING
    // SYNCED
    // FAILED
    // ========================================================

    store.createIndex(
      'syncStatus',
      'syncStatus',
      {
        unique:
          false
      }
    )

    // ========================================================
    // OPENED
    // ========================================================

    store.createIndex(
      'openedAtUtc',
      'openedAtUtc',
      {
        unique:
          false
      }
    )

    // ========================================================
    // UPDATED
    // ========================================================

    store.createIndex(
      'updatedAtUtc',
      'updatedAtUtc',
      {
        unique:
          false
      }
    )
  }


  // ==========================================================
  // SUPPORT CHECK
  // ==========================================================

  isSupported() {
    return (
      typeof window !==
        'undefined' &&
      'indexedDB' in window
    )
  }


  // ==========================================================
  // GET OBJECT STORE
  // ==========================================================

  async getStore(
    storeName,
    mode = 'readonly'
  ) {
    const db =
      await this.open()

    if (
      !db.objectStoreNames.contains(
        storeName
      )
    ) {
      throw new Error(
        `[IndexedDbManager] Object store "${storeName}" does not exist.`
      )
    }

    const transaction =
      db.transaction(
        storeName,
        mode
      )

    return transaction.objectStore(
      storeName
    )
  }


  // ==========================================================
  // GET
  // ==========================================================

  async get(
    storeName,
    key
  ) {
    const store =
      await this.getStore(
        storeName,
        'readonly'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.get(
            key
          )

        request.onsuccess =
          () => {
            resolve(
              request.result
              ??
              null
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB get failed.',
              {
                storeName,
                key,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // GET ALL
  // ==========================================================

  async getAll(
    storeName
  ) {
    const store =
      await this.getStore(
        storeName,
        'readonly'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.getAll()

        request.onsuccess =
          () => {
            resolve(
              request.result
              ??
              []
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB getAll failed.',
              {
                storeName,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // GET ALL BY INDEX
  // ==========================================================

  async getAllByIndex(
    storeName,
    indexName,
    value
  ) {
    const store =
      await this.getStore(
        storeName,
        'readonly'
      )

    const index =
      store.index(
        indexName
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          index.getAll(
            value
          )

        request.onsuccess =
          () => {
            resolve(
              request.result
              ??
              []
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB indexed read failed.',
              {
                storeName,
                indexName,
                value,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // ADD
  // ==========================================================

  async add(
    storeName,
    value
  ) {
    const store =
      await this.getStore(
        storeName,
        'readwrite'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.add(
            value
          )

        request.onsuccess =
          () => {
            eventBus.emit(
              'storage.indexeddb.changed',
              {
                operation:
                  'add',

                storeName,

                key:
                  request.result
              }
            )

            resolve(
              request.result
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB add failed.',
              {
                storeName,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // PUT
  // ==========================================================

  async put(
    storeName,
    value
  ) {
    const store =
      await this.getStore(
        storeName,
        'readwrite'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.put(
            value
          )

        request.onsuccess =
          () => {
            eventBus.emit(
              'storage.indexeddb.changed',
              {
                operation:
                  'put',

                storeName,

                key:
                  request.result
              }
            )

            resolve(
              request.result
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB put failed.',
              {
                storeName,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // DELETE
  // ==========================================================

  async delete(
    storeName,
    key
  ) {
    const store =
      await this.getStore(
        storeName,
        'readwrite'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.delete(
            key
          )

        request.onsuccess =
          () => {
            eventBus.emit(
              'storage.indexeddb.changed',
              {
                operation:
                  'delete',

                storeName,

                key
              }
            )

            resolve(
              true
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB delete failed.',
              {
                storeName,
                key,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // CLEAR
  // ==========================================================

  async clear(
    storeName
  ) {
    const store =
      await this.getStore(
        storeName,
        'readwrite'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.clear()

        request.onsuccess =
          () => {
            eventBus.emit(
              'storage.indexeddb.changed',
              {
                operation:
                  'clear',

                storeName
              }
            )

            resolve(
              true
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB clear failed.',
              {
                storeName,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // COUNT
  // ==========================================================

  async count(
    storeName
  ) {
    const store =
      await this.getStore(
        storeName,
        'readonly'
      )

    return new Promise(
      (
        resolve,
        reject
      ) => {
        const request =
          store.count()

        request.onsuccess =
          () => {
            resolve(
              Number(
                request.result
                ??
                0
              )
            )
          }

        request.onerror =
          () => {
            logger.error(
              'IndexedDB count failed.',
              {
                storeName,

                error:
                  request.error
              }
            )

            reject(
              request.error
            )
          }
      }
    )
  }


  // ==========================================================
  // HAS STORE
  // ==========================================================

  async hasStore(
    storeName
  ) {
    const db =
      await this.open()

    return db.objectStoreNames
      .contains(
        storeName
      )
  }


  // ==========================================================
  // GET DATABASE INFORMATION
  // ==========================================================

  async getInfo() {
    const db =
      await this.open()

    return {
      dbName:
        db.name,

      version:
        db.version,

      stores:
        Array.from(
          db.objectStoreNames
        )
    }
  }


  // ==========================================================
  // CLOSE
  // ==========================================================

  close() {
    if (this.db) {
      this.db.close()
    }

    this.db =
      null

    this.openPromise =
      null
  }
}


const indexedDb =
  new IndexedDbManager()


export default indexedDb